// Parameters.js — de pop-up met alle waarden waarmee het model rekent.
//
// Twee uitgangspunten:
// 1. Niets wordt hier met de hand overgetypt. De rekenkernwaarden komen uit
//    gemeenteData.js, de netwaarden uit de backend, en de plan-instellingen
//    krijgt dit venster als prop mee vanuit het scherm. Zo kan deze lijst
//    nooit iets anders zeggen dan waarmee gerekend wordt.
// 2. Het tabblad Rekenwaarden van het model is alleen te lezen. Het tabblad
//    Instellingen van de gemeente (sinds 27 september 2026) is invulbaar: elke
//    waarde gaat langs dezelfde setter en bewaarfunctie als op het tabblad
//    waar hij thuishoort. Of iemand mag wijzigen, beslist de server, net als
//    op die tabbladen.
import React, { useEffect, useMemo, useState } from 'react';
import {
  JAARKM, VERBRUIK_PER_KM, STADSFACTOR,
  EV_AANDEEL_VLAANDEREN, WELVAARTSINDEX_VLAANDEREN,
  FLUVIUS_ONDERREGISTRATIE, FLUVIUS_KAPPA, PRIVE_DALING_PER_JAAR, WERK_PRIVE_PCT,
  SEMI_GEWICHTSFACTOR, WIJKTYPE_MIX, DOELGROEP_LAADTYPE,
  AC_NORM_KWH_REEKS, MOW_DREMPEL_BIJPLAATSING_KWH,
  dcUtilisatieMwhMaand, hpcUtilisatieMwhMaand,
  SOCKET_KW_SLIM, DEKKING_AFSTAND_M,
  WERKNEMERS_PER_HA, FORENZEN_KM_PER_DAG, WERKDAGEN_PER_JAAR,
  VLOOT_DREMPEL,
} from './gemeenteData';
import { getNetcapaciteitParameters } from './api';

const N = {
  ink:'#12161C', ink2:'#3D4652', ink3:'#6B7684', ink4:'#9AA4B1',
  paper:'#FFFFFF', paper2:'#FBFAF7', line:'#D9D5CD', line2:'#C4BFB5',
  head:'#F6F4F0', accent:'#1F5C6B', accent2:'#2D7F92', warn:'#9C6420',
};

const kom = (v, d = 1) => Number(v).toFixed(d).replace('.', ',');
const pct = (v, d = 0) => `${kom(v * 100, d)} %`;
// Ontbreekt een groep of een waarde in gemeenteData.js, dan staat er '…' in
// plaats van dat het hele venster stukloopt.
const drie = (o, sleutels) => (o
  ? sleutels.map(s => (o[s] == null ? '…' : Math.round(o[s] * 100))).join(' · ')
  : '…');

// Deze twee waarden staan als deel van één in de database: 0,7 betekent 70
// procent. Een ambtenaar leest hele procenten, dus zo komen ze in beeld.
function deelPct(waarde) {
  if (waarde == null) return '…';
  const pct = Number(waarde) * 100;
  return `${Math.round(pct * 10) / 10} %`.replace('.', ',');
}

// Datum als 27-08-2026. Geen tijdstip: voor de verantwoording telt de dag.
function datumKort(waarde) {
  if (!waarde) return '';
  const d = new Date(waarde);
  if (isNaN(d.getTime())) return String(waarde);
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
}


// ── Tabblad Instellingen van de gemeente ─────────────────────────────
// Alle waarden die een gemeente zelf kiest, op één plek en in de volgorde van
// de werksessies. Een wijziging hier gaat langs precies dezelfde weg als op
// het tabblad zelf: dezelfde setter, dezelfde bewaarfunctie, dezelfde
// grenzen. Er komt dus geen tweede opslag bij en een tabblad kan nooit iets
// anders tonen dan dit overzicht.

// Een getal dat pas bij het verlaten van het veld (of Enter) wordt gezet, zodat
// de berekening niet bij elke toets opnieuw draait. Komma en punt mogen allebei.
function GetalVeld({ waarde, onZet, min, max, breed = 88, decimalen = 0, leegMag = false, label, placeholder }) {
  const toon = (w) => (w == null || w === '' ? '' : (decimalen
    ? Number(w).toFixed(decimalen).replace('.', ',')
    : Math.round(Number(w)).toLocaleString('nl-BE')));
  const [tekst, setTekst] = useState(toon(waarde));
  useEffect(() => { setTekst(toon(waarde)); }, [waarde]);
  const zet = () => {
    const ruw = String(tekst).trim();
    // Met decimalen is een punt of komma het decimaalteken; zonder decimalen
    // is een punt het duizendtal-teken, zoals in 7.500.
    const schoon = decimalen ? ruw.replace(',', '.') : ruw.replace(/\./g, '').replace(',', '.');
    if (schoon === '') {
      if (leegMag) { if (waarde != null) onZet(null); }
      else setTekst(toon(waarde));
      return;
    }
    let n = parseFloat(schoon);
    if (!Number.isFinite(n)) { setTekst(toon(waarde)); return; }
    if (min != null) n = Math.max(min, n);
    if (max != null) n = Math.min(max, n);
    n = decimalen ? Math.round(n * 10 ** decimalen) / 10 ** decimalen : Math.round(n);
    setTekst(toon(n));
    if (n !== waarde) onZet(n);
  };
  return (
    <input type="text" inputMode="decimal" aria-label={label} value={tekst} placeholder={placeholder}
      onChange={e => setTekst(e.target.value)} onBlur={zet}
      onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      style={{ width: breed, height: 36, boxSizing: 'border-box', padding: '0 10px', fontSize: 15,
               fontFamily: 'inherit', color: N.ink, textAlign: 'right', background: N.paper,
               border: `1px solid ${N.line}`, borderRadius: 6 }} />
  );
}

const GOUD = '#B8923C';
function Stip({ aan }) {
  return (
    <span title={aan ? 'Afwijkend van de standaard' : undefined}
      style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, display: 'inline-block',
               background: aan ? GOUD : 'transparent' }} />
  );
}
function Tab({ naam }) {
  return (
    <span style={{ fontSize: 13, color: N.accent, background: '#E6EFF0', borderRadius: 4,
                   padding: '3px 8px', whiteSpace: 'nowrap' }}>{naam}</span>
  );
}
const RIJ_KOLOMMEN = 'minmax(0,1fr) 240px 160px 110px';
function Rij({ naam, uitleg, gewijzigd, invoer, eenheid, standaard, tab }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: RIJ_KOLOMMEN, gap: 16, alignItems: 'center',
                  padding: '11px 0', borderBottom: '1px solid #ECE9E3' }}>
      <div>
        <div style={{ fontSize: 15, fontWeight: 600, color: N.ink, lineHeight: 1.35 }}>{naam}</div>
        {uitleg && <div style={{ fontSize: 13, color: N.ink3, lineHeight: 1.4, marginTop: 2 }}>{uitleg}</div>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Stip aan={gewijzigd} />{invoer}
        {eenheid && <span style={{ fontSize: 15, color: N.ink2 }}>{eenheid}</span>}
      </div>
      <div style={{ fontSize: 14, color: N.ink3 }}>{standaard}</div>
      <div>{tab && <Tab naam={tab} />}</div>
    </div>
  );
}
function Blok({ titel, moment, children }) {
  return (
    <section style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                    paddingBottom: 6, borderBottom: `1px solid ${N.line}` }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: N.ink }}>{titel}</div>
        {moment && <div style={{ fontSize: 13, color: N.ink3 }}>{moment}</div>}
      </div>
      {children}
    </section>
  );
}
function Knop({ aan, onClick, children }) {
  return (
    <button type="button" onClick={onClick}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14, fontFamily: 'inherit',
               color: aan ? '#fff' : N.ink, background: aan ? N.accent : N.paper,
               border: `1px solid ${aan ? N.accent : N.line}`, borderRadius: 16, padding: '5px 12px',
               cursor: 'pointer' }}>
      {children}
    </button>
  );
}
const eur = (n) => `${Math.round(n).toLocaleString('nl-BE')} euro`;
const zelfde = (a, b) => JSON.stringify([...(a || [])].sort()) === JSON.stringify([...(b || [])].sort());

const BLOKKEN = [
  ['behoefte', 'Behoefte'], ['bestaand', 'Bestaande laadpunten'], ['pleinen', 'Laadpleinen'],
  ['plaatsing', 'Plaatsing'], ['net', 'Net en slim laden'], ['kosten', 'Kosten'], ['stad', 'Gegevens van de stad'],
];

function InstellingenBlad({ i }) {
  const S = i.standaard || {};
  const jaren = [];
  for (let j = 2027; j <= 2035; j++) jaren.push(j);
  const normVan = (jr) => (i.normPerJaar && i.normPerJaar[jr] != null ? i.normPerJaar[jr] : AC_NORM_KWH_REEKS[jr]);
  const normGewijzigd = !!(i.normPerJaar && Object.keys(i.normPerJaar).length);
  const [actief, setActief] = useState('behoefte');
  const ga = (id) => {
    setActief(id);
    const el = document.getElementById(`param-${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const pctPrive = i.priveStart != null ? i.priveStart * 100 : null;
  const berekendPct = Math.round((i.priveBerekend ?? 0.5) * 100);

  return (
    <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
      <nav style={{ width: 210, flexShrink: 0, borderRight: `1px solid ${N.line}`, background: N.paper2,
                    padding: '14px 10px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {BLOKKEN.map(([id, naam]) => (
          <button key={id} type="button" onClick={() => ga(id)}
            style={{ textAlign: 'left', fontSize: 15, fontFamily: 'inherit', padding: '8px 12px', borderRadius: 6,
                     border: 'none', cursor: 'pointer', color: actief === id ? '#fff' : N.ink2,
                     background: actief === id ? N.accent : 'transparent' }}>{naam}</button>
        ))}
      </nav>

      <div style={{ flex: 1, overflow: 'auto', padding: '16px 24px 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: RIJ_KOLOMMEN, gap: 16, fontSize: 13, fontWeight: 700,
                      color: N.ink3, paddingBottom: 12 }}>
          <div>Parameter</div><div>Waarde</div><div>Standaard</div><div>Werkt op</div>
        </div>

        <div id="param-behoefte"><Blok titel="Behoefte" moment="Werksessie 1">
          <Rij naam="Aandeel privé-laden, startpunt 2026"
            uitleg="Deel van de laadvraag dat thuis of op het werk wordt opgevangen. Leeg laten volgt het berekende startpunt."
            gewijzigd={i.priveStart != null}
            invoer={<GetalVeld label="Aandeel privé-laden" waarde={pctPrive} min={0} max={100} leegMag
                      placeholder={String(berekendPct)}
                      onZet={v => i.setPriveStart(v == null ? null : v / 100)} />}
            eenheid="%" standaard={`berekend ${berekendPct} %`} tab="Dashboard" />
          <Rij naam="Privé laadpunten (Fluvius)"
            uitleg="Basis voor het berekende startpunt. Vastgezet blijven de getallen van het plan gelijk."
            gewijzigd={!!i.fluviusVast}
            invoer={<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
              <span style={{ fontSize: 15, color: N.ink, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
                  {i.fluviusVast && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={N.accent} strokeWidth="2.4" strokeLinecap="round">
                      <rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 018 0v4"/>
                    </svg>
                  )}
                  <b>{i.fluviusVast ? `~${Math.round(i.fluviusVast.aantal * FLUVIUS_KAPPA).toLocaleString('nl-BE')}`
                      : (i.fluviusLive != null ? `~${Math.round(i.fluviusLive * FLUVIUS_KAPPA).toLocaleString('nl-BE')}` : '…')}</b>
                  privé laadpunten
                </span>
                <span style={{ fontSize: 13, color: N.ink3 }}>{i.fluviusVast ? i.fluviusDatum : 'live'}</span>
              </span>
              <Knop aan={false} onClick={i.fluviusVastzetten}>{i.fluviusVast ? 'Actualiseren' : 'Vastzetten'}</Knop>
            </div>}
            standaard={<span>live bij Fluvius
              {i.fluviusVast && i.fluviusLive != null && i.fluviusLive !== i.fluviusVast.aantal && (
                <span style={{ display: 'block', fontSize: 12.5, color: '#B4442F' }}>
                  nu ~{Math.round(i.fluviusLive * FLUVIUS_KAPPA).toLocaleString('nl-BE')}</span>
              )}</span>}
            tab="Dashboard" />
          <Rij naam={'Aandeel elektrische auto\u2019s'}
            uitleg="Een eigen reeks per jaar wordt ingevuld via Bewerken."
            gewijzigd={!!i.evEigen}
            invoer={<span style={{ fontSize: 15, color: N.ink }}>{i.evEigen ? 'eigen cijfers' : 'Vlaams gemiddelde'}</span>}
            standaard="Vlaams gemiddelde" tab="Dashboard" />
          <Rij naam="Buffer voor pieken en storingen" gewijzigd={i.redundantieMarge !== S.redundantieMarge}
            invoer={<GetalVeld label="Buffer" waarde={i.redundantieMarge * 100} min={0} max={30}
                      onZet={v => i.setRedundantieMarge(v / 100)} />}
            eenheid="%" standaard={`standaard ${Math.round((S.redundantieMarge ?? 0.1) * 100)} %`} tab="Dashboard" />
          <div style={{ padding: '11px 0', borderBottom: '1px solid #ECE9E3' }}>
            <div style={{ display: 'grid', gridTemplateColumns: RIJ_KOLOMMEN, gap: 16, alignItems: 'start' }}>
              <div style={{ gridColumn: '1 / 4' }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: N.ink }}>Afzet per laadpunt per jaar</div>
                <div style={{ fontSize: 13, color: N.ink3, marginTop: 2 }}>
                  In kWh. Hoeveel stroom een publiek laadpunt moet leveren om als goed gebruikt te gelden.</div>
              </div>
              <div><Tab naam="Gemeente" /></div>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginTop: 10 }}>
              <Stip aan={normGewijzigd} />
              {jaren.map(jr => (
                <div key={jr} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontSize: 12.5, color: N.ink3 }}>{jr}</span>
                  <GetalVeld label={`Afzet per laadpunt ${jr}`} breed={68} waarde={normVan(jr)}
                    onZet={v => i.bewaarNorm(jr, v)} />
                </div>
              ))}
            </div>
            <div style={{ fontSize: 13, color: N.ink3, marginTop: 8, paddingLeft: 16 }}>
              {`Standaard: ${AC_NORM_KWH_REEKS[2027].toLocaleString('nl-BE')} kWh in 2027 oplopend tot ${AC_NORM_KWH_REEKS[2035].toLocaleString('nl-BE')} kWh in 2035`}
            </div>
            {i.normMelding && (
              <div style={{ fontSize: 13, color: N.warn, marginTop: 6, paddingLeft: 16 }}>{i.normMelding}</div>
            )}
          </div>
        </Blok></div>

        <div id="param-bestaand"><Blok titel="Bestaande laadpunten" moment="Werksessie 2">
          <Rij naam="Gepland te installeren, normaal laden" uitleg="Laadpunten die de concessiehouder nog plaatst."
            gewijzigd={i.huidigLP !== S.huidigLP}
            invoer={<GetalVeld label="Gepland te installeren" waarde={i.huidigLP} min={0} onZet={v => i.setHuidigLP(v)} />}
            eenheid="laadpunten" standaard={`standaard ${S.huidigLP ?? 0}`} tab="Dashboard" />
          <Rij naam="Laadpunten die de bijplaatsnorm halen"
            uitleg="Meer dan 1.000 kWh per maand of meer dan 65 procent bezetting. Leeg: elk nieuw laadpunt krijgt een eigen aansluiting."
            gewijzigd={i.pvpNormPct != null}
            invoer={<GetalVeld label="Laadpunten die de bijplaatsnorm halen" waarde={i.pvpNormPct} min={0} max={100}
                      leegMag placeholder="leeg" onZet={v => i.bewaarPvp(v)} />}
            eenheid="%" standaard="leeg" tab="Plaatsing" />
        </Blok></div>

        <div id="param-pleinen"><Blok titel="Laadpleinen" moment="Werksessie 2">
          <Rij naam="Minimaal aantal parkeervakken" uitleg="Voor een kandidaatlocatie."
            gewijzigd={i.pleinMinVak !== S.pleinMinVak}
            invoer={<GetalVeld label="Minimaal aantal parkeervakken" waarde={i.pleinMinVak} min={6} max={100}
                      onZet={v => i.setPleinMinVak(v)} />}
            eenheid="vakken" standaard={`standaard ${S.pleinMinVak}`} tab="Laadpleinen" />
          <Rij naam="Laadpunten per laadplein" gewijzigd={i.pleinOmvang !== S.pleinOmvang}
            invoer={<GetalVeld label="Laadpunten per laadplein" waarde={i.pleinOmvang} min={6} max={20}
                      onZet={v => i.setPleinOmvang(v)} />}
            eenheid="laadpunten" standaard={`standaard ${S.pleinOmvang}`} tab="Laadpleinen" />
          <Rij naam="Aandeel nieuwe laadpunten op een laadplein" gewijzigd={i.pleinAandeel !== S.pleinAandeel}
            invoer={<GetalVeld label="Aandeel op een laadplein" waarde={i.pleinAandeel} min={0} max={100}
                      onZet={v => i.setPleinAandeel(v)} />}
            eenheid="%" standaard={`standaard ${S.pleinAandeel} %`} tab="Laadpleinen" />
          <Rij naam="Loopafstand naar een laadplein"
            uitleg="Een laadplein vangt tot en met het laatste jaar van de periode eerst de nieuwe laadpunten van de eigen wijk op, vanaf het jaar van plaatsing. Wat daarna over is, neemt losse laadpunten over in andere wijken binnen deze afstand, gepland na het jaar van plaatsing."
            gewijzigd={i.pleinLoop !== S.pleinLoop}
            invoer={<GetalVeld label="Loopafstand naar een laadplein" waarde={i.pleinLoop} min={50} max={1000}
                      onZet={v => i.setPleinLoop(v)} />}
            eenheid="m" standaard={`standaard ${S.pleinLoop} m`} tab="Laadpleinen" />
          <Rij naam="Maximale afstand tot een voorziening" gewijzigd={i.pleinNabij !== S.pleinNabij}
            invoer={<GetalVeld label="Maximale afstand" waarde={i.pleinNabij} min={50} max={800}
                      onZet={v => i.setPleinNabij(v)} />}
            eenheid="m" standaard={`standaard ${S.pleinNabij} m`} tab="Laadpleinen" />
          <Rij naam="Parkeervakken" uitleg="Semipubliek zijn winkel- en bedrijfsparkings."
            gewijzigd={!zelfde(i.pleinToegang, S.pleinToegang)}
            invoer={<div style={{ display: 'flex', gap: 6 }}>
              {[['openbaar', 'Openbaar'], ['semipubliek', 'Semipubliek']].map(([id, naam]) => (
                <Knop key={id} aan={i.pleinToegang.includes(id)}
                  onClick={() => i.setPleinToegang(h => h.includes(id) ? h.filter(x => x !== id) : [...h, id])}>{naam}</Knop>
              ))}</div>}
            standaard="Openbaar" tab="Laadpleinen" />
          <div style={{ display: 'grid', gridTemplateColumns: RIJ_KOLOMMEN, gap: 16, alignItems: 'center',
                        padding: '11px 0', borderBottom: '1px solid #ECE9E3' }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: N.ink }}>Soorten voorzieningen in de buurt</div>
              <div style={{ fontSize: 13, color: N.ink3, marginTop: 2 }}>Zonder keuze telt de afstand niet mee.</div>
            </div>
            <div style={{ gridColumn: '2 / 4', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Stip aan={!zelfde(i.pleinSoorten, S.pleinSoorten)} />
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {(i.soorten || []).map(([id, naam]) => (
                  <Knop key={id} aan={i.pleinSoorten.includes(id)}
                    onClick={() => i.setPleinSoorten(h => h.includes(id) ? h.filter(x => x !== id) : [...h, id])}>{naam}</Knop>
                ))}
              </div>
            </div>
            <div><Tab naam="Laadpleinen" /></div>
          </div>
        </Blok></div>

        <div id="param-plaatsing"><Blok titel="Plaatsing" moment="Werksessie 2">
          <Rij naam="Voorrang voor plekken zonder laadpunt binnen 250 meter"
            uitleg="1,0 is uit: de palen gaan naar de grootste vraag, ook waar al laadpunten staan."
            gewijzigd={i.plaatsVoorrang !== S.plaatsVoorrang}
            invoer={<GetalVeld label="Voorrang" decimalen={1} waarde={i.plaatsVoorrang / 100} min={1} max={2.5}
                      onZet={v => i.setPlaatsVoorrang(Math.round(v * 100))} />}
            eenheid="keer zo zwaar" standaard={`standaard ${kom((S.plaatsVoorrang ?? 150) / 100)}`} tab="Plaatsing" />
          {i.gisAanwezig && <Rij naam="Maximale afstand tot het net" uitleg="Voor een losse laadpaal: afstand tot de straatkabel, volgens het GIS-bestand van de netbeheerder."
            gewijzigd={i.netGrens !== S.netGrens}
            invoer={<GetalVeld label="Maximale afstand tot het net" waarde={i.netGrens} min={5} max={200}
                      onZet={v => i.setNetGrens(v)} />}
            eenheid="m" standaard={`standaard ${S.netGrens} m`} tab="Plaatsing" />}
        </Blok></div>

        <div id="param-net"><Blok titel="Net en slim laden" moment="Werksessie 3">
          <Rij naam="Slim laden" uitleg="Deel van de laadpalen waarop slim laden wordt toegepast."
            gewijzigd={i.netcapSlim !== S.netcapSlim}
            invoer={<GetalVeld label="Slim laden" waarde={i.netcapSlim} min={0} max={100} onZet={v => i.setNetcapSlim(v)} />}
            eenheid="% van de laadpalen" standaard={`standaard ${S.netcapSlim ?? 0} %`} tab="Netcapaciteit" />
          <Rij naam="Planperiode" uitleg="Geldt voor tabblad Netcapaciteit en voor het venster Wijk impact op tabblad Laadpleinen."
            gewijzigd={i.netcapBegin !== S.netcapBegin || i.netcapEind !== S.netcapEind}
            invoer={<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {[['Eerste jaar', i.netcapBegin, i.setNetcapBegin], null, ['Laatste jaar', i.netcapEind, i.setNetcapEind]]
                .map((v, k) => v === null
                  ? <span key={k} style={{ fontSize: 15, color: N.ink2 }}>tot en met</span>
                  : (
                  <select key={k} aria-label={v[0]} value={v[1]} onChange={e => v[2](Number(e.target.value))}
                    style={{ height: 36, padding: '0 8px', fontSize: 15, fontFamily: 'inherit', color: N.ink,
                             background: N.paper, border: `1px solid ${N.line}`, borderRadius: 6 }}>
                    {jaren.map(j => <option key={j} value={j}>{j}</option>)}
                  </select>
                ))}</div>}
            standaard={`${S.netcapBegin} tot en met ${S.netcapEind}`} tab="Netcapaciteit" />
        </Blok></div>

        <div id="param-kosten"><Blok titel="Kosten" moment="Werksessie 3">
          {[['AC', 'Investering per gewoon laadpunt'], ['DC', 'Investering per snellader van twee laadpunten'],
            ['HPC', 'Investering per ultrasnellader van twee laadpunten']].map(([k, naam]) => (
            <Rij key={k} naam={naam} gewijzigd={i.capex?.[k] !== S.capex?.[k]}
              invoer={<GetalVeld label={naam} breed={100} waarde={i.capex?.[k]} min={0}
                        onZet={v => i.setCapex(c => ({ ...c, [k]: v }))} />}
              eenheid="euro" standaard={`standaard ${eur(S.capex?.[k] ?? 0)}`} tab="Dashboard" />
          ))}
        </Blok></div>

        <div id="param-stad"><Blok titel="Gegevens van de stad" moment="Alleen lezen, wijzigen via Bewerken">
          <div style={{ display: 'flex', gap: 12, paddingTop: 12 }}>
            {[['Inwoners', i.stad?.inwoners], ['Personenwagens', i.stad?.voertuigen], ['Wijken', i.stad?.wijken],
              ['Publieke laadpunten volgens MOW', i.stad?.mow]].map(([naam, n]) => (
              <div key={naam} style={{ flex: 1, background: N.head, borderRadius: 8, padding: '12px 16px' }}>
                <div style={{ fontSize: 13, color: N.ink3 }}>{naam}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: N.ink }}>
                  {n == null ? '…' : Number(n).toLocaleString('nl-BE')}</div>
              </div>
            ))}
          </div>
        </Blok></div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: N.ink3 }}>
          <Stip aan />Afwijkend van de standaard.
        </div>
      </div>
    </div>
  );
}

// ── Uitleg bij de groei van het aantal auto's ────────────────────────
// Rekent zelf niets: de factoren en het groeitempo komen van de server
// (huishoudens.js), het aantal auto's van de telling uit de gemeente. Zo staat
// hier precies waarmee het model rekent. Basisjaar 2027: het eerste jaar van
// het plan. Teksten vastgesteld door HL op 21 oktober 2026.
const BASISJAAR_PLAN = 2027;
const LAATSTE_PLANJAAR = 2035;

function AutoGroeiUitleg({ st, groei, voertuigen, gemeenteNaam }) {
  const f = groei?.factoren;
  const reeks = groei?.reeks || {};
  const B = groei?.basisjaar, T = groei?.laatsteTelling;
  const naam = gemeenteNaam || 'deze gemeente';
  const n = (v) => (v == null ? '…' : Math.round(Number(v)).toLocaleString('nl-BE'));
  const autos = (j) => (voertuigen != null && f?.[j] != null) ? n(Number(voertuigen) * f[j]) : '…';
  const helling = Number(groei?.helling) || 0;
  const tempo = helling > 0 ? `Voor ${naam} komen er zo elk jaar ${n(helling)} huishoudens bij.`
    : helling < 0 ? `Voor ${naam} gaan er zo elk jaar ${n(-helling)} huishoudens af.`
    : `Voor ${naam} blijft het aantal huishoudens zo gelijk.`;
  const jaren = [...new Set([B, T, BASISJAAR_PLAN, 2030, LAATSTE_PLANJAAR].filter(j => j != null))].sort((a, b) => a - b);

  return (
    <div style={st.uitleg}>
      <div style={st.uitlegK}>Hoe het aantal auto’s per jaar meegroeit</div>
      {!f ? (
        <p style={st.uitlegP}>
          De groei van het aantal auto’s is voor {naam} niet bekend. {groei?.reden || ''} Het
          model rekent daarom elk jaar met het aantal auto’s van de telling.
        </p>
      ) : (
        <>
          <p style={st.uitlegP}>
            Het aantal auto’s is geteld in {B}: de auto’s die bij een huishouden in de
            gemeente horen, volgens Statbel, plus 9,96 procent voor auto’s die op naam van een
            bedrijf staan maar ’s nachts in de gemeente blijven. Voor {naam} is dat {n(voertuigen)}.
          </p>
          <p style={st.uitlegP}>
            Het aantal auto’s per huishouden blijft gelijk aan die telling. Het aantal auto’s
            in een jaar volgt dus het aantal huishoudens:
          </p>
          <ul style={{ ...st.uitlegP, paddingLeft: 20 }}>
            <li>tot en met {T} het getelde aantal huishoudens uit het Rijksregister.</li>
            <li>Het groeitempo van de laatste zes tellingen ({groei?.trendVan}-{groei?.trendTot}) is
              doorgetrokken naar {BASISJAAR_PLAN}-{LAATSTE_PLANJAAR}. {tempo}</li>
          </ul>
          {groei?.reden && <p style={st.uitlegP}>{groei.reden}</p>}
          <table style={st.uitlegT}>
            <thead>
              <tr>
                <td style={{ ...st.uT, fontWeight: 700 }}>Jaar</td>
                <td style={{ ...st.uT, fontWeight: 700 }}>Herkomst</td>
                <td style={{ ...st.uTr, fontWeight: 700, paddingRight: 16 }}>Huishoudens</td>
                <td style={{ ...st.uTr, fontWeight: 700 }}>Auto’s</td>
              </tr>
            </thead>
            <tbody>
              {jaren.map(j => (
                <tr key={j}>
                  <td style={st.uT}>{j === BASISJAAR_PLAN ? `${j}, basisjaar` : j}</td>
                  <td style={st.uT}>{reeks[j]?.herkomst || ''}</td>
                  <td style={{ ...st.uTr, paddingRight: 16 }}>{n(reeks[j]?.huishoudens)}</td>
                  <td style={st.uTr}>{autos(j)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

export default function Parameters({ onClose, plan = {}, net = {}, herkomst = {}, instellingen = null, gemeenteNaam = '',
                                     autoGroei = null, voertuigen = null }) {
  const [blad, setBlad] = useState(instellingen ? 'instellingen' : 'rekenwaarden');
  const [backend, setBackend] = useState(null);
  const [fout, setFout] = useState(null);
  const [zoek, setZoek] = useState('');
  const [groep, setGroep] = useState(null);

  useEffect(() => {
    let weg = false;
    getNetcapaciteitParameters()
      .then(r => { if (!weg) setBackend(r); })
      // Zichtbaar falen: zonder de backendwaarden kan dit venster niet zeggen
      // waarmee het tabblad Netcapaciteit rekent, en dat moet je weten.
      .catch(e => { if (!weg) setFout(e.message); });
    return () => { weg = true; };
  }, []);

  // Wijkt de backend af van wat het scherm zelf gebruikt? Dan hoort dat hier
  // te staan, want dan zeggen twee tabbladen iets anders over dezelfde paal.
  const afwijkingen = useMemo(() => {
    if (!backend || !net.PAAL_VERMOGEN) return [];
    const uit = [];
    const kijk = (naam, hier, daar) => {
      if (hier == null || daar == null) return;
      if (String(hier) !== String(daar)) uit.push(`${naam}: scherm ${hier}, server ${daar}`);
    };
    for (const soort of ['ac', 'dc', 'hpc']) {
      kijk(`aansluitwaarde ${soort} laag`, net.PAAL_VERMOGEN[soort]?.kvaMin, backend.paalVermogen?.[soort]?.kvaMin);
      kijk(`aansluitwaarde ${soort} hoog`, net.PAAL_VERMOGEN[soort]?.kvaMax, backend.paalVermogen?.[soort]?.kvaMax);
    }
    kijk('laadpunten per paal', net.PUNTEN_PER_PAAL, backend.puntenPerPaal);
    kijk('grens studie', net.KVA_STUDIE, backend.kvaStudie);
    kijk('grens cabine', net.KVA_CABINE, backend.kvaCabine);
    kijk('vermogen per laadpunt met slim laden', SOCKET_KW_SLIM, backend.slim?.kwPerPuntSlim);
    return uit;
  }, [backend, net]);

  // De groei van de afzet bij snelladers wordt afgeleid uit de functie zelf,
  // zodat er geen tweede plek ontstaat waar dat percentage staat.
  const groeiDc = (jaar) => {
    const nu = dcUtilisatieMwhMaand(jaar), ervoor = dcUtilisatieMwhMaand(jaar - 1);
    return ervoor ? Math.round((nu / ervoor - 1) * 100) : 0;
  };

  const pv = backend?.paalVermogen || net.PAAL_VERMOGEN || {};
  const groepen = [
    ['Aansluiting op het net', [
      ['Aansluitwaarde gewone laadpaal', pv.ac ? `${kom(pv.ac.kvaMax)} kVA` : '…', pv.ac?.aansluiting || ''],
      ['Aansluitwaarde snellader', pv.dc ? `${kom(pv.dc.kvaMax)} kVA` : '…', pv.dc?.aansluiting || ''],
      ['Aansluitwaarde ultrasnellader',
       pv.hpc ? `${kom(pv.hpc.kvaMin, 0)} tot ${kom(pv.hpc.kvaMax, 0)} kVA` : '…', pv.hpc?.aansluiting || ''],
      ['Laadpunten per laadpaal', String(backend?.puntenPerPaal ?? net.PUNTEN_PER_PAAL ?? 2), ''],
      ['Grens vanaf waar Fluvius na een studie beslist: laagspanning of middenspanning',
       `${backend?.kvaStudie ?? net.KVA_STUDIE ?? '…'} kVA`, 'Fluvius'],
      ['Grens waarboven een eigen cabine nodig is',
       `${backend?.kvaCabine ?? net.KVA_CABINE ?? '…'} kVA`, 'Fluvius'],
      ['Gegarandeerd vermogen per laadpunt met slim laden',
       `${backend?.slim?.kwPerPuntSlim ?? SOCKET_KW_SLIM} kW`, ''],
      ['Laadpunten op een aansluiting met slim laden',
       String(backend?.slim?.puntenPerAansluiting ?? '…'), ''],
      ['Peildatum aansluitwaarden', backend?.peildatum || net.VERMOGEN_PEILDATUM || '…', ''],
    ]],
    ['Instellingen van deze gemeente', [
      ['Aandeel privé-laden, startpunt', deelPct(plan.priveStart), '', true, 'priveStart'],
      ['Buffer voor pieken en storingen', deelPct(plan.redundantieMarge), '', true, 'redundantieMarge'],
      ['Gepland te installeren, AC', plan.huidigLP != null ? String(plan.huidigLP) : '…',
       '', true, 'huidigLP'],
      ['Voorrang bij het plaatsen', plan.plaatsVoorrang || '…', '', true, 'plaatsVoorrang'],
      ...(plan.gisAanwezig ? [['Maximale afstand van een losse laadpaal tot het net', `${plan.netGrens ?? '…'} m`, '', true, 'netGrens']] : []),
    ]],
    ['Laadpleinen en locaties', [
      ['Minimaal aantal parkeervakken voor een kandidaatlocatie', String(plan.minVak ?? '…'), '', true, 'pleinMinVak'],
      ['Aantal laadpunten per laadplein', String(plan.pleinOmvang ?? '…'), '', true, 'pleinOmvang'],
      ['Aandeel nieuwe laadpunten dat op een laadplein landt', `${plan.pleinAandeel ?? '…'} %`, '', true, 'pleinAandeel'],
      ['Maximale afstand tot een voorziening', `${plan.nabij ?? '…'} m`, '', true, 'pleinNabij'],
      ['Loopafstand naar een laadplein', `${plan.pleinLoop ?? '…'} m`, '', true, 'pleinLoop'],
    ]],
    ['Kosten', [
      ['Investering per gewoon laadpunt', plan.capex ? `€ ${plan.capex.AC.toLocaleString('nl-BE')}` : '…', '', true, 'capex'],
      ['Investering per snellader van twee laadpunten', plan.capex ? `€ ${plan.capex.DC.toLocaleString('nl-BE')}` : '…', '', true, 'capex'],
      ['Investering per ultrasnellader van twee laadpunten', plan.capex ? `€ ${plan.capex.HPC.toLocaleString('nl-BE')}` : '…', '', true, 'capex'],
    ]],
    ['Normen', [
      ['Maximale loopafstand tot een laadpaal', `${DEKKING_AFSTAND_M} m`, 'VR-besluit dec. 2025'],
      ['Hoe zwaar een semipubliek laadpunt meetelt', kom(SEMI_GEWICHTSFACTOR), 'MOW Bijlage 11'],
    ]],
    ['Voertuigen en energie', [
      ['Jaarkilometrage per elektrisch voertuig', `${JAARKM.toLocaleString('nl-BE')} km`, ''],
      ['Verbruik per kilometer, inclusief laadverliezen', `${kom(VERBRUIK_PER_KM, 2)} kWh`, ''],
      ['Stadsfactor voor kortere stedelijke ritten', kom(STADSFACTOR, 2), ''],
      ['Groei van het aantal auto\u2019s', 'volgt de huishoudens',
       'Rijksregister, getelde huishoudens per gemeente'],
      ['Aandeel elektrische voertuigen Vlaanderen in 2025, 2030 en 2035',
       [2025, 2030, 2035].map(j => kom(EV_AANDEEL_VLAANDEREN[j] * 100)).join(' · ') + ' %',
       'Fluvius Investeringsplan', true],
      ['Welvaartsindex Vlaanderen', kom(WELVAARTSINDEX_VLAANDEREN), 'Statbel', true],
    ]],
    ['Privé tegenover publiek', [
      ['Privé-laadpunten die niet zijn aangemeld', pct(FLUVIUS_ONDERREGISTRATIE), ''],
      ['Vermenigvuldiging op het aantal privé-laadpunten dat bij Fluvius bekend is', kom(FLUVIUS_KAPPA, 4), ''],
      ['Jaarlijkse daling van het privé-aandeel', `${kom(PRIVE_DALING_PER_JAAR * 100, 0)} procentpunt`, ''],
      ['Aandeel bedrijfswagens met een thuislader', pct(WERK_PRIVE_PCT), ''],
    ]],
    ['Doelgroepen en laadgedrag', [
      ['Verdeling bewoners, bezoekers en logistiek in een binnenstad', drie(WIJKTYPE_MIX.binnenstad, ['bew','bez','log']), ''],
      ['Verdeling bewoners, bezoekers en logistiek in een woonwijk', drie(WIJKTYPE_MIX.woonwijk, ['bew','bez','log']), ''],
      ['Verdeling bewoners, bezoekers en logistiek op een bedrijventerrein', drie(WIJKTYPE_MIX.bedrijventerrein, ['bew','bez','log']), ''],
      ['Verdeling normaal, snel en ultrasnel bij bewoners', drie(DOELGROEP_LAADTYPE.bew, ['ac','dc','hpc']), ''],
      ['Verdeling normaal, snel en ultrasnel bij bezoekers', drie(DOELGROEP_LAADTYPE.bez, ['ac','dc','hpc']), ''],
      ['Verdeling normaal, snel en ultrasnel bij logistiek', drie(DOELGROEP_LAADTYPE.log, ['ac','dc','hpc']), ''],
    ]],
    ['Van energie naar laadpunten', [
      ['Afzet per gewoon laadpunt per jaar, standaardreeks',
       `${AC_NORM_KWH_REEKS[2027].toLocaleString('nl-BE')} tot ${AC_NORM_KWH_REEKS[2035].toLocaleString('nl-BE')} kWh`,
       'Loopt op van 2027 naar 2035, van de helft van de MOW-drempel naar 83 procent daarvan. Geldt als een gemeente niets invult. Per jaar in te stellen op tabblad Gemeente', true],
      ['MOW-drempel bijplaatsing',
       `${MOW_DREMPEL_BIJPLAATSING_KWH.toLocaleString('nl-BE')} kWh`,
       'Boven 1.000 kWh per laadpunt per maand plaatst de concessiehouder bij'],
      ['Afzet per snellaadpunt per maand, in 2026', `${kom(dcUtilisatieMwhMaand(2026))} MWh`, ''],
      ['Afzet per ultrasnellaadpunt per maand, in 2026', `${kom(hpcUtilisatieMwhMaand(2026))} MWh`, ''],
      ['Jaarlijkse groei van de afzet per snellaadpunt',
       `${groeiDc(2028)} · ${groeiDc(2030)} · ${groeiDc(2034)} %`, ''],
    ]],
    ['Bedrijventerrein', [
      ['Werknemers per hectare bedrijventerrein', kom(WERKNEMERS_PER_HA), 'Rebel'],
      ['Woon-werkafstand per dag, retour', `${FORENZEN_KM_PER_DAG} km`, 'SD Worx'],
      ['Werkdagen per jaar', String(WERKDAGEN_PER_JAAR), ''],
    ]],
    ['Leasewagens in een gemeente', [
      ['Ingeschreven auto\u2019s gedeeld door auto\u2019s van gezinnen',
       `${kom(VLOOT_DREMPEL, 1)} of hoger`, ''],
      ['Wat er dan gebeurt',
       'waarschuwing bij het invulveld voor een eigen EV-percentage', ''],
    ]],
  ];

  const q = zoek.trim().toLowerCase();
  const zichtbaar = groepen
    .filter(([g]) => !groep || groep === g)
    .map(([g, rijen]) => [g, rijen.filter(r => !q || `${r[0]} ${r[2]}`.toLowerCase().includes(q))])
    .filter(([, rijen]) => rijen.length);

  const st = {
    waas: { position:'fixed', inset:0, background:'rgba(18,22,28,0.42)', display:'flex',
            alignItems:'center', justifyContent:'center', padding:34, zIndex:2000 },
    venster: { background:N.paper, borderRadius:12, width:'min(1180px,100%)', height:'min(740px,100%)',
               display:'flex', flexDirection:'column', overflow:'hidden',
               boxShadow:'0 18px 60px rgba(0,0,0,0.28)' },
    kop: { padding:'18px 24px 0', borderBottom:`1px solid ${N.line}` },
    inp: { fontSize:13.5, padding:'7px 10px', border:`1px solid ${N.line}`, borderRadius:7,
           fontFamily:'inherit', color:N.ink, background:N.paper, width:300 },
    lnk: { fontSize:13, color:N.accent, background:'none', border:'none', fontFamily:'inherit', cursor:'pointer' },
    zij: { width:230, flexShrink:0, borderRight:`1px solid ${N.line}`, background:N.paper2,
           overflow:'auto', padding:'12px 0' },
    zijK: (aan) => ({ display:'block', width:'100%', textAlign:'left', fontSize:13,
      color: aan ? N.ink : N.ink2, fontWeight: aan ? 700 : 400, background: aan ? N.head : 'none',
      border:'none', fontFamily:'inherit', cursor:'pointer', padding:'7px 20px', lineHeight:1.35,
      boxShadow: aan ? `inset 2px 0 0 ${N.accent2}` : 'none' }),
    lijst: { flex:1, overflow:'auto', padding:'0 24px 24px' },
    groepK: { fontSize:11.5, letterSpacing:'.07em', color:N.ink3, textTransform:'uppercase',
              padding:'20px 0 8px', position:'sticky', top:0, background:N.paper },
    rij: { display:'grid', gridTemplateColumns:'minmax(0,1fr) 186px 176px', gap:16,
           alignItems:'baseline', padding:'9px 0', borderTop:`1px solid ${N.line}` },
    voet: { borderTop:`1px solid ${N.line}`, background:N.paper2, padding:'11px 24px',
            display:'flex', alignItems:'center', gap:14, fontSize:12.5, color:N.ink3 },
    knop: { fontSize:13, padding:'7px 14px', borderRadius:7, border:`1px solid ${N.accent}`,
            background:N.accent, color:'#fff', fontFamily:'inherit', cursor:'pointer' },
    uitleg:  { background:N.paper2, border:`1px solid ${N.line}`, borderRadius:9, padding:'14px 16px', margin:'18px 0 4px' },
    uitlegK: { fontSize:14.5, fontWeight:700, color:N.ink, marginBottom:6 },
    uitlegK2:{ fontSize:12.5, fontWeight:700, color:N.ink2, margin:'14px 0 4px' },
    uitlegP: { fontSize:13, color:N.ink2, lineHeight:1.55, margin:'0 0 8px' },
    uitlegT: { borderCollapse:'collapse', fontSize:13 },
    uT:      { padding:'3px 16px 3px 0', color:N.ink2 },
    uTr:     { padding:'3px 0', color:N.ink, textAlign:'right', whiteSpace:'nowrap' },
    tag: { display:'inline-block', fontSize:11, padding:'1px 7px', borderRadius:20, marginLeft:8,
           border:`1px solid ${N.line2}`, color:N.ink3, background:N.head, whiteSpace:'nowrap' },
  };

  return (
    <div style={st.waas} onClick={onClose}>
      <div style={st.venster} onClick={e => e.stopPropagation()}>

        <div style={st.kop}>
          <div style={{ display:'flex', alignItems:'baseline', gap:12 }}>
            <h2 style={{ margin:0, fontSize:19, fontWeight:700, letterSpacing:'-0.02em' }}>
              {gemeenteNaam ? `Parameters van ${gemeenteNaam}` : 'Parameters'}</h2>
            <span style={{ fontSize:13, color:N.ink3 }}>
              {blad === 'instellingen' ? 'Wijzigingen gelden meteen op alle tabbladen.' : 'De waarden waarmee het model rekent'}</span>
            <span style={{ flex:1 }} />
            <button onClick={onClose} style={{ fontSize:20, color:N.ink3, background:'none',
              border:'none', cursor:'pointer', lineHeight:1 }}>×</button>
          </div>
          {instellingen && (
            <div style={{ display:'flex', gap:4, marginTop:12 }}>
              {[['instellingen', `Instellingen van ${gemeenteNaam || 'de gemeente'}`], ['rekenwaarden', 'Rekenwaarden van het model']]
                .map(([id, naam]) => (
                <button key={id} type="button" onClick={() => setBlad(id)}
                  style={{ padding:'9px 14px', fontFamily:'inherit', fontSize:15, cursor:'pointer', background:'none',
                           border:'none', borderBottom:`3px solid ${blad === id ? N.accent : 'transparent'}`,
                           color: blad === id ? N.accent : N.ink3, fontWeight: blad === id ? 700 : 400 }}>{naam}</button>
              ))}
            </div>
          )}
          {blad === 'rekenwaarden' && (
          <div style={{ display:'flex', alignItems:'center', gap:10, margin:'14px 0 12px' }}>
            <input style={st.inp} placeholder="Zoeken in parameters"
              value={zoek} onChange={e => setZoek(e.target.value)} />
            <span style={{ flex:1 }} />
            <button style={st.lnk} onClick={() => { setGroep(null); setZoek(''); }}>Alles tonen</button>
          </div>
          )}
          {fout && (
            <div style={{ fontSize:13, color:'#B4442F', paddingBottom:12 }}>
              De aansluitwaarden konden niet bij de server worden opgehaald: {fout}
            </div>
          )}
          {afwijkingen.length > 0 && (
            <div style={{ fontSize:13, color:N.warn, background:'#FBF3E2',
                          border:'1px solid #E4D2A8', borderRadius:7, padding:'8px 11px', marginBottom:12 }}>
              Let op: het scherm en de server rekenen niet met dezelfde waarden.{' '}
              {afwijkingen.join('; ')}.
            </div>
          )}
        </div>

        {blad === 'instellingen' && instellingen && <InstellingenBlad i={instellingen} />}

        {blad === 'rekenwaarden' && (
        <div style={{ flex:1, display:'flex', minHeight:0 }}>
          <nav style={st.zij}>
            {groepen.map(([g, rijen]) => (
              <button key={g} style={st.zijK(groep === g)}
                onClick={() => setGroep(groep === g ? null : g)}>
                {g} <span style={{ color:N.ink4, fontWeight:400 }}>{rijen.length}</span>
              </button>
            ))}
          </nav>

          <div style={st.lijst}>
            {/* Uitleg bij de norm per laadpunt. Staat bovenaan omdat dit het
                getal is waar een gemeente op stuurt en waar de meeste vragen
                over komen. Het rekenvoorbeeld gebruikt de gemeente die op dat
                moment geopend is, dus het klopt altijd met wat er op het
                scherm staat. */}
            {/* Uitleg bij de norm per laadpunt. Staat bovenaan omdat dit het getal
                is waar een gemeente op stuurt en waar de meeste vragen over komen.
                Het rekenvoorbeeld gebruikt ronde getallen, geen echte gemeente:
                dit venster rekent zelf niets uit en toont alleen de waarden waar
                het model mee werkt. */}
            {(!groep || groep === 'Voertuigen en energie') && !q && (
              <AutoGroeiUitleg st={st} groei={autoGroei} voertuigen={voertuigen} gemeenteNaam={gemeenteNaam} />
            )}
            {(!groep || groep === 'Van energie naar laadpunten') && !q && (
              <div style={st.uitleg}>
                <div style={st.uitlegK}>Hoeveel stroom er per laadpunt doorheen gaat</div>
                <p style={st.uitlegP}>
                  Het model deelt de publieke laadvraag door dit getal om te bepalen hoeveel
                  laadpunten er nodig zijn. Hoe hoger het staat, hoe minder laadpunten er nodig zijn.
                </p>
                <p style={st.uitlegP}>
                  De concessiehouder plaatst een paal bij zodra een laadpunt boven 1.000 kWh per
                  maand komt, dus boven {MOW_DREMPEL_BIJPLAATSING_KWH.toLocaleString('nl-BE')} kWh
                  per jaar. Dat is de MOW-drempel bijplaatsing. De norm ligt daaronder, zodat er
                  ruimte blijft voor groei binnen het jaar.
                </p>
                <p style={st.uitlegP}>
                  De standaardreeks loopt van {AC_NORM_KWH_REEKS[2027].toLocaleString('nl-BE')} kWh
                  in 2027 naar {AC_NORM_KWH_REEKS[2035].toLocaleString('nl-BE')} in 2035, met 500 kWh
                  per jaar erbij. Dat is van de helft van de drempel naar 83 procent daarvan. De
                  reeks loopt op omdat het aantal elektrische auto's sneller groeit dan het aantal
                  laadpunten dat een gemeente kan plaatsen, dus wordt het per laadpunt drukker.
                  Elke gemeente kan de norm per jaar aanpassen op tabblad Gemeente.
                </p>

                <div style={st.uitlegK2}>Rekenvoorbeeld met ronde getallen, norm 7.500 kWh</div>
                <table style={st.uitlegT}>
                  <tbody>
                    <tr><td style={st.uT}>publieke laadvraag</td>
                        <td style={st.uTr}>10.000 MWh</td></tr>
                    <tr><td style={st.uT}>waarvan gewoon laden, 82 procent</td>
                        <td style={st.uTr}>8.200 MWh</td></tr>
                    <tr><td style={st.uT}>gedeeld door de norm van 7,5 MWh</td>
                        <td style={st.uTr}>1.093</td></tr>
                    <tr><td style={st.uT}>plus de buffer van 5 procent</td>
                        <td style={st.uTr}>1.148</td></tr>
                    <tr><td style={st.uT}>plus snel en ultrasnel laden</td>
                        <td style={st.uTr}>28</td></tr>
                    <tr><td style={{...st.uT, fontWeight:700, borderTop:`1px solid ${N.line}`}}>samen</td>
                        <td style={{...st.uTr, fontWeight:700, borderTop:`1px solid ${N.line}`}}>1.176 laadpunten</td></tr>
                  </tbody>
                </table>
                <p style={st.uitlegP}>
                  Snel en ultrasnel laden nemen 18 procent van de energie op maar leveren maar
                  2 procent van de laadpunten, want een snellaadpunt verzet acht keer zoveel
                  als een gewoon laadpunt.
                </p>
                <p style={st.uitlegP}>
                  Het aantal elektrische auto's dat één publiek laadpunt deelt, loopt door de jaren
                  heen op: de auto's groeien met 11 tot 20 procent per jaar, de norm met 5 tot 8
                  procent, en het verschil daartussen is wat er aan laadpunten bij komt.
                </p>
              </div>
            )}
            {zichtbaar.map(([g, rijen]) => (
              <div key={g}>
                <div style={st.groepK}>{g}</div>
                {rijen.map(([naam, waarde, bron, perPlan, sleutel]) => {
                  // Is dit uitgangspunt door iemand aangepast, dan staat eronder
                  // wie dat deed en wanneer. Zonder die regel ziet een collega
                  // alleen het getal en niet dat het verzet is.
                  const wie = sleutel ? herkomst[sleutel] : null;
                  return (
                  <div key={naam} style={st.rij}>
                    <div style={{ fontSize:14.5, lineHeight:1.35 }}>
                      {naam}
                      {perPlan && <span style={st.tag}>per plan in te stellen</span>}
                    </div>
                    <div style={{ textAlign:'right' }}>
                      <div style={{ fontSize:15, fontWeight:700, whiteSpace:'nowrap' }}>{waarde}</div>
                      {wie && wie.door && (
                        <div style={{ fontSize:12.5, color:N.ink3, marginTop:3, whiteSpace:'nowrap' }}>
                          Gewijzigd door {wie.door} op {datumKort(wie.wanneer)}
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize:12.5, color:N.ink3, lineHeight:1.35 }}>{bron}</div>
                  </div>
                  );
                })}
              </div>
            ))}
            {!zichtbaar.length && (
              <div style={{ fontSize:14, color:N.ink3, paddingTop:24 }}>Niets gevonden.</div>
            )}
          </div>
        </div>

        )}

        <div style={st.voet}>
          <span>Peildatum aansluitwaarden {backend?.peildatum || net.VERMOGEN_PEILDATUM || 'onbekend'}</span>
          <span style={{ flex:1 }} />
          <button style={st.knop} onClick={onClose}>Sluiten</button>
        </div>

      </div>
    </div>
  );
}
